import React from 'react';
import { useSelector } from 'react-redux';
import { selectHasPermission } from '../../../../features/auth/authSlice.js';
import OmButton from '../../../../components/ui/OmButton';
import OmSelect from '../../../../components/ui/OmSelect';
import OmSelectOption from '../../../../components/ui/OmSelectOption';
import OmTextField from '../../../../components/ui/OmTextField';
import OmDatePicker from '../../../../components/ui/OmDatePicker';
import ExportButton from './ExportButton';

/**
 * AuditLogFilters Component
 * 
 * Renders the filter section for audit logs with all input controls and action buttons.
 * Replaces legacy HTML form elements with OM Design System components while
 * preserving exact same filter logic and validation behavior.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter state
 * @param {string} props.filters.actionType - Selected action type filter
 * @param {string} props.filters.startDate - Start date filter value
 * @param {string} props.filters.endDate - End date filter value
 * @param {Function} props.onFilterChange - Handler for filter value changes
 * @param {Function} props.onApplyFilters - Handler for applying filters
 * @param {Function} props.onClearFilters - Handler for clearing all filters
 * @param {Function} props.onExportCSV - Handler for CSV export
 * @param {boolean} props.exportLoading - Whether export is in progress
 */
const AuditLogFilters = ({
  filters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  onExportCSV,
  exportLoading = false
}) => {
  // Check export permission
  const canExport = useSelector(state => selectHasPermission(state, 'audit_logs:read'));

  /**
   * Get action type options for the select dropdown
   * Maintains exact same options as legacy component
   */
  const getActionTypeOptions = () => [
    { value: '', label: 'All Actions' },
    { value: 'CLAIM_CREATED', label: 'Claim Created' },
    { value: 'CLAIM_UPDATED', label: 'Claim Updated' },
    { value: 'CLAIM_APPROVED', label: 'Claim Approved' },
    { value: 'CLAIM_REJECTED', label: 'Claim Rejected' },
    { value: 'STATUS_CHANGE', label: 'Status Change' },
    { value: 'DOCUMENT_UPLOAD', label: 'Document Upload' },
    { value: 'SLA_STARTED', label: 'SLA Started' },
    { value: 'SLA_WARNING', label: 'SLA Warning' },
    { value: 'SLA_BREACH', label: 'SLA Breach' }
  ];

  /**
   * Handle filter input changes
   */
  const handleInputChange = (field, value) => {
    if (onFilterChange) {
      onFilterChange(field, value);
    }
  };

  /**
   * Get active filter summary for display
   */
  const getFilterSummary = () => {
    const activeFilters = [];
    
    if (filters.actionType) {
      const actionLabel = getActionTypeOptions().find(opt => opt.value === filters.actionType)?.label;
      if (actionLabel && actionLabel !== 'All Actions') {
        activeFilters.push(`Action: ${actionLabel}`);
      }
    }
    
    if (filters.startDate) {
      activeFilters.push(`From: ${filters.startDate}`);
    }
    
    if (filters.endDate) {
      activeFilters.push(`To: ${filters.endDate}`);
    }
    
    return activeFilters;
  };

  const activeFilters = getFilterSummary();

  return (
    <div className="card mb-8" style={{ borderRadius: '0.5rem' }}>
      <div className="card-body">
        {/* Action Buttons */}
        <div className="mb-4 flex gap-3">
          <OmButton
            buttonType="filled-button"
            buttonSize="medium"
            onClick={onApplyFilters}
          >
            Apply Filters
          </OmButton>
          
          <OmButton
            buttonType="outlined-button"
            buttonSize="medium"
            onClick={onClearFilters}
          >
            Clear Filters
          </OmButton>
          
          {canExport && (
            <ExportButton
              onExport={onExportCSV}
              isLoading={exportLoading}
            />
          )}
        </div>

        {/* Active Filters Summary */}
        {activeFilters.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-600">Active filters:</span>
            {activeFilters.map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {filter}
              </span>
            ))}
          </div>
        )}
        
        {/* Filter Input Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Action Type Filter */}
          <div className="space-y-2">
            <div style={{
              '--omk-select-text-field-container-color': '#ffffff',
              '--omk-select-text-field-label-text-color': '#006250',
              '--omk-select-text-field-input-text-color': '#334155',
              '--omk-select-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
              '--omk-select-text-field-focus-outline-color': '#56a353'
            }}>
              <OmSelect
                label="Action Type"
                value={filters.actionType}
                onChange={(e) => handleInputChange('actionType', e.target.value)}
              >
                {getActionTypeOptions().map((option) => (
                  <OmSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </OmSelectOption>
                ))}
              </OmSelect>
            </div>
          </div>

          {/* Start Date Filter */}
          <div className="space-y-2">
            <div style={{
              '--omk-text-field-container-color': '#ffffff',
              '--omk-text-field-label-text-color': '#006250',
              '--omk-text-field-input-text-color': '#334155',
              '--omk-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
              '--omk-text-field-focus-outline-color': '#56a353',
              '--omk-text-field-caret-color': '#56a353'
            }}>
              <OmDatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
              />
            </div>
          </div>

          {/* End Date Filter */}
          <div className="space-y-2">
            <div style={{
              '--omk-text-field-container-color': '#ffffff',
              '--omk-text-field-label-text-color': '#006250',
              '--omk-text-field-input-text-color': '#334155',
              '--omk-text-field-outline-color': 'rgba(86, 163, 83, 0.3)',
              '--omk-text-field-focus-outline-color': '#56a353',
              '--omk-text-field-caret-color': '#56a353'
            }}>
              <OmDatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogFilters;